<template>
  <div
    class="content print:avoid-page-break mb-6 last:mb-0"
    v-if="canManage || attachments.length > 0"
  >
    <div class="mt-3 flex px-6 py-4">
      <h1 class="text-lg">{{ $t('attachments.title') }}</h1>
      <div v-show="loading !== null" class="ml-6 text-gray-500">
        <i class="fal fa-circle-notch fa-spin mr-2"></i>
        <span>{{ loading }}...</span>
      </div>
    </div>
    <div class="flex flex-col justify-start bg-white p-6">
      <div class="-mb-2 flex-wrap sm:flex" v-if="attachments.length > 0">
        <div
          class="mb-2 flex items-center justify-start rounded bg-gray-100 px-4 py-3 sm:mr-2 sm:max-w-1/3"
          v-for="{ isPending, name, type, path, created, size, uuid } in attachments"
          :key="uuid"
        >
          <span class="fal mr-4 text-2xl text-brand-500" :class="fileIconClass(type)"></span>
          <div class="flex min-w-0 flex-1 flex-col items-start leading-snug">
            <span v-if="isPending" class="mb-0 w-full truncate font-semibold text-gray-500">
              {{ name }}
            </span>
            <a
              v-else
              :href="path"
              class="mb-0 w-full truncate font-semibold text-black hover:text-brand-500"
            >
              {{ name }}
            </a>
            <span class="mt-1 text-sm text-gray-500"> {{ created }} | {{ size }} </span>
          </div>
          <span
            v-if="!isPending && canManage"
            class="far fa-times ml-5 -mr-1 cursor-pointer p-3 text-gray-400 hover:text-gray-700"
            @click.prevent="deleteAttachment(uuid)"
          ></span>
        </div>
      </div>
      <template v-if="canManage">
        <hr class="mt-5 mb-6" v-if="attachments.length > 0" />
        <input type="file" @change="uploadFile()" ref="file" :disabled="progress" />
        <p class="mt-3 text-brand-500" v-if="error">
          {{ error }}
        </p>
        <div class="-mx-6 -mb-7 mt-6 h-1 bg-gray-200" v-if="progress">
          <div
            class="h-1 bg-brand-500"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
            :style="{ width: progress + '%' }"
          ></div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  props: {
    ownerType: {
      type: String,
      required: true,
    },
    ownerKey: {
      type: [String, Number],
      required: false,
    },
    canManage: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: null,
      attachments: [],
      progress: null,
      error: null,
    }
  },
  methods: {
    fileIconClass(type) {
      const overrides = {
        photo: 'image',
      }

      if (type && type !== 'file') {
        return 'fa-file-' + (overrides[type] || type)
      }

      return 'fa-file'
    },
    loadAttachments() {
      this.loading = this.$t('attachments.loading')

      return axios
        .get(
          this.route('app_api.attachments', {
            attachmentSubjectType: this.ownerType,
            attachmentSubjectKey: this.ownerKey,
          })
        )
        .then(({ data }) => {
          this.attachments = data
        })
        .catch(() => {
          this.attachments = []
        })
        .finally(() => {
          this.progress = null
          this.error = false
          this.loading = null
        })
    },
    uploadFile() {
      let input = this.$refs.file
      let file = input.files[0]

      if (file.size > 1024 * 1024 * 50) {
        this.error = this.$t('attachments.max_filesize_exceeded')
        input.value = ''
        return
      }
      this.error = null

      Vapor.store(file, {
        progress: (progress) => {
          this.progress = Math.round(progress * 100)
        },
      })
        .then((response) => {
          axios
            .post(
              this.route('app_api.attachments.store', {
                attachmentSubjectType: this.ownerType,
                attachmentSubjectKey: this.ownerKey,
              }),
              {
                uuid: response.uuid,
                name: file.name,
                type: file.type,
              }
            )
            .then(() => {
              this.loadAttachments()
            })
            .catch(() => {
              this.progress = null
              this.error = this.$t('attachments.uploading_failed')
            })
            .finally(() => {
              input.value = ''
            })
        })
        .catch(() => {
          this.error = this.$t('attachments.uploading_failed')
          this.progress = null
          input.value = ''
        })
    },
    deleteAttachment(uuid) {
      if (this.loading) {
        return
      }

      if (!confirm(this.$t('attachments.confirm_deletion'))) {
        return
      }

      this.error = null
      this.loading = this.$t('attachments.deleting')

      axios
        .delete(
          this.route('app_api.attachments.destroy', {
            attachment: uuid,
          })
        )
        .then(() => {
          this.loadAttachments()
        })
        .catch(() => {
          this.error = this.$t('attachments.deletion_failed')
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
  },
  mounted() {
    this.loadAttachments()
  },
}
</script>
